/**
 * ConfirmDialogProvider — global, imperative branded AlertDialog.
 *
 *   import { confirm } from "@/components/ui/confirm-dialog";
 *
 *   const ok = await confirm({
 *     title: "Discard changes?",
 *     description: "You have unsaved changes. Closing will lose them.",
 *     confirmText: "Discard",
 *     cancelText: "Keep editing",
 *     tone: "danger", // "danger" | "warn" | "info" (default)
 *   });
 *   if (ok) { ... }
 *
 * Implementation:
 *   <ConfirmDialogProvider /> is mounted ONCE at the app root. It exposes
 *   an internal singleton that `confirm()` resolves to. Each invocation
 *   replaces the current dialog state with the new prompt + a fresh
 *   Promise. Returns a Promise<boolean>.
 *
 *   Resolves false on Cancel + Esc + outside-click (we explicitly tell
 *   Radix's AlertDialog to allow outside-click dismissal of the confirm
 *   itself — it's a confirmation, not a form, so losing nothing).
 */
import { useEffect, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AlertTriangle, Info, ShieldAlert } from "lucide-react";

// ---------- internal singleton ----------
let pushDialog = null;

/**
 * Imperative API. Returns Promise<boolean>.
 * If the provider isn't mounted yet, falls back to native confirm() so we
 * never silently swallow the dialog.
 */
export function confirm(opts = {}) {
  if (!pushDialog) {
    if (typeof window !== "undefined") return Promise.resolve(window.confirm(opts.description || opts.title || "Are you sure?"));
    return Promise.resolve(false);
  }
  return new Promise((resolve) => pushDialog({ ...opts, resolve }));
}

// ---------- provider component ----------
const TONE_PALETTE = {
  danger: { icon: ShieldAlert, color: "text-rose-500",   btn: "bg-rose-600 hover:bg-rose-700 text-white" },
  warn:   { icon: AlertTriangle, color: "text-amber-500", btn: "bg-amber-600 hover:bg-amber-700 text-white" },
  info:   { icon: Info,          color: "text-purple-500", btn: "bg-purple-600 hover:bg-purple-700 text-white" },
};

export default function ConfirmDialogProvider() {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState(null);
  // Keep the latest resolver in a ref so we can call it from event handlers
  // that may fire after a state change has settled the dialog props.
  const resolverRef = useRef(null);

  useEffect(() => {
    pushDialog = (next) => {
      // If a previous dialog is somehow still open, resolve it false first
      // so its caller isn't left hanging.
      if (resolverRef.current) resolverRef.current(false);
      resolverRef.current = next.resolve;
      setState({
        title: next.title || "Are you sure?",
        description: next.description || "",
        confirmText: next.confirmText || "Confirm",
        cancelText: next.cancelText || "Cancel",
        tone: next.tone || "info",
      });
      setOpen(true);
    };
    return () => { pushDialog = null; };
  }, []);

  const resolve = (value) => {
    setOpen(false);
    const r = resolverRef.current;
    resolverRef.current = null;
    if (r) r(value);
  };

  if (!state) {
    return (
      <AlertDialog open={open} onOpenChange={(v) => { if (!v) resolve(false); }}>
        <AlertDialogContent />
      </AlertDialog>
    );
  }

  const palette = TONE_PALETTE[state.tone] || TONE_PALETTE.info;
  const Icon = palette.icon;

  return (
    <AlertDialog open={open} onOpenChange={(v) => { if (!v) resolve(false); }}>
      <AlertDialogContent
        className="max-w-md border-purple-200 dark:border-purple-900/40 bg-white dark:bg-slate-900"
        data-testid="branded-confirm-dialog"
      >
        <AlertDialogHeader>
          <div className="flex items-start gap-3">
            <div className={`mt-0.5 ${palette.color}`}><Icon className="w-6 h-6" /></div>
            <div className="flex-1">
              <AlertDialogTitle className="text-slate-900 dark:text-white" data-testid="confirm-title">{state.title}</AlertDialogTitle>
              {state.description && (
                <AlertDialogDescription className="text-slate-500 dark:text-slate-400 mt-1" data-testid="confirm-description">
                  {state.description}
                </AlertDialogDescription>
              )}
            </div>
          </div>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => resolve(false)} data-testid="confirm-cancel">
            {state.cancelText}
          </AlertDialogCancel>
          <AlertDialogAction
            className={palette.btn}
            onClick={() => resolve(true)}
            data-testid="confirm-ok"
          >
            {state.confirmText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
